import { Buffer } from 'buffer';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ReactComponent as Close } from '../../assets/svg/CloseRed.svg';
import { ReactComponent as FileShadow } from '../../assets/svg/File.svg';
import { FSObjectCategory, useDeleteContentTrailerMutation, useDeleteFileMutation, useDeleteUploadV1Mutation, useLazyGetFilesQuery, useUploadMultipartMutation } from '../../services/content_v1';
import { BASE_URL } from '../../services/reauth';
import { useAlert } from '../alert';
import { extractErrorDetail, getCrc32 } from '../common';
import FormEntryComment from './FormEntryComment';
import FormEntryStatus from './FormEntryStatus';
import FormUploaderZone from './FormUploaderZone';
import { ErrorModal } from './Modals/ErrorModal';


const FormUploaderZoneHint = () => <>
    <Trans>
        Drag file here
    </Trans>
    <br />
    <span>
        <Trans>
            or select on computer
        </Trans>
    </span>
</>;

interface UnitTrailerUploaderProps {
    has: boolean,
    label: string,
    accept?: string,
    delete_url?: string,
    onDelete?: () => void,
    show_url?: string,
    onChange?: (uid: number) => void,
    blocked?: boolean,
    blockedMsg?: React.ReactNode,
    // модерация
    checked: boolean | null,
    setChecked?: (v: boolean | null) => void,
    comment: string | undefined,
    setComment?: (v: string | undefined) => void,
    moderateable: boolean,
    moderation?: boolean,
    ageLimit?: boolean,
    account_id?: number,
}

function UnitTrailerUploaderV1({ has, blocked, blockedMsg, ageLimit, label, accept, delete_url, onDelete, show_url, onChange, checked, setChecked, comment, setComment, moderateable, moderation, account_id }: UnitTrailerUploaderProps) {
    const alert = useAlert();
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const [getFiles, { data: uploaded }] = useLazyGetFilesQuery();
    const [fetchUploadMultipart] = useUploadMultipartMutation();
    const [deleteTrailer] = useDeleteContentTrailerMutation();
    const [deleteUpload] = useDeleteUploadV1Mutation();
    const [deleteFile] = useDeleteFileMutation();

    const refUpload = useRef<HTMLInputElement | null>(null);
    const abortController = useRef(new AbortController());
    const [error, setError] = useState('');
    const [poll, setPoll] = useState(false);
    const [fileId, setFileId] = useState<number | null>(null);
    const [unsaved, setUnsaved] = useState('');
    const [fileUUID, setFileUUID] = useState('');
    const [errorTitle] = useState<string>(t('Trailer'));

    const [progress, setProgress] = useState<number | null>(null);
    const [filename, setFilename] = useState('');
    const [active, setActive] = useState(false);

    const uploadPart = async (uuid: string, file: File, chunkIndex: number, chunkCount: number, cat: FSObjectCategory, signal?: AbortSignal) => {
        const from = (chunkIndex - 1) * CHUNK_SIZE;
        const to = from + CHUNK_SIZE;

        const fdata = await file.slice(from, to).arrayBuffer();
        const chunk = Buffer.from(fdata);
        const crc = await getCrc32(fdata);

        return new Promise((resolve, reject) => {
            if (signal?.aborted) {
                reject(new DOMException('Operation aborted', 'AbortError'));
            }

            signal?.addEventListener('abort', () => {
                reject(new DOMException('Operation aborted', 'AbortError'));
            });

            fetchUploadMultipart({
                uuid: uuid,
                account_id: account_id,
                data: chunk,
                chunkCount,
                chunkIndex,
                orig_name: file.name,
                fullsize: file.size,
                crc: crc,
                chunk_size: chunk.length,
                cat,
                signal,
            }).unwrap().then((r) => resolve(r)).catch((e) => reject(e));
        });
    }

    const CHUNK_SIZE = 5 * 1024 * 1024;
    const CONCURRENT = 2;

    const uploadMultipart = async (f: File, cat: FSObjectCategory, missed_chunks: number[][], ac?: AbortController) => {
        const chunksCount = Math.ceil(f.size / CHUNK_SIZE);
        const uuid = uuidv4();
        setFileUUID(uuid);

        let errCount = 0;

        let missedChunks: number[] = [];
        if (missed_chunks && missed_chunks.length > 0) {
            const chunckList: number[] = [];
            missed_chunks.every(([start, stop]) => {
                for (let i = start; i <= stop; i += 1) {
                    chunckList.push(i);
                }
                return true;
            });

            missedChunks = chunckList;
        } else {
            missed_chunks = [[1, chunksCount]];
            const chunckList: number[] = [];
            missed_chunks.every(([start, stop]) => {
                for (let i = start; i <= stop; i += 1) {
                    chunckList.push(i);
                }
                return true;
            });
            missedChunks = chunckList;
        }

        async function worker(fn: (arg: number) => Promise<unknown>, missedChunksLink: number[]) {
            const chunkIndex = missedChunksLink.shift();
            if (!chunkIndex) {
                return;
            }
            if (ac?.signal.aborted) {
                missedChunksLink.push(chunkIndex);
                return;
            }
            try {
                const p = Math.floor((chunksCount - missedChunks.length) / chunksCount * 100);
                setProgress(p);
                /*const result = */await fn(chunkIndex);
            } catch (e) {
                errCount += 1;
                console.warn(e);
                if (errCount > 2) {
                    throw e;
                }
                missedChunksLink.push(chunkIndex);
            }
            return await worker(fn, missedChunksLink);
        }

        const doWork = async (chunkIndex: number) => {
            return uploadPart(uuid, f, chunkIndex, chunksCount, cat, ac?.signal);
        }

        const task = new Array(CONCURRENT).fill(true);
        const tasks = [...task.map(() => worker(doWork, missedChunks))];

        Promise.all(tasks)
            .then(() => {
                if (!ac?.signal.aborted) {
                    getFiles({ offset: 0, limit: 1, upload_uuid: uuid });
                    // if (chunkIndex) {
                    //     uploadPart(f, chunkIndex, chunksCount, cat, abortController?.signal);
                    // }
                }
            })
            .catch((e) => {
                setError(extractErrorDetail(e));
                // throw Error(e);
            });
    }

    useEffect(() => {
        if (uploaded?.results.data) {
            const file = uploaded.results.data[0];
            if (file) {
                setFileId(file.id);
                setUnsaved(file.signedurl);
                setProgress(null);
                onChange && onChange(file.id);
            } else {
                setTimeout(() => getFiles({ offset: 0, limit: 1, upload_uuid: fileUUID }), 500);
            }
        }
    }, [uploaded]);

    useEffect(() => {
        if (has) {
            setUnsaved('');
        }
    }, [has]);

    useEffect(() => {
        if (poll) {
            // pollUpdates();
        }
        // eslint-disable-next-line
    }, [poll]);

    // useEffect(() => {
    //     if (data?.status.code === 0) {
    //         // const unitDetail = data?.results.data;
    //         // setMC(unitDetail.missed_chunks);

    //         //setLocalStatus({...localStatus, missed_chunks: unitDetail.missed_chunks});
    //     }
    // }, [data]);

    function validateTrailerFile(f: File) {
        if (f.size > 1024 * 1024 * 1024) {
            // more 1024Mb
            return false;
        }
        const extention = f.name.split('.').pop() || '';
        if (!['mp4', 'webm', 'ogg'].includes(extention.toLocaleLowerCase()) && !sessionStorage.skipFileCheck) {
            return false;
        }
        return true;
    }

    async function remove() {
        abortController.current.abort();

        deleteUpload(fileUUID).unwrap().then(() => {
            setProgress(null);
        }).catch((err) => {
            setError(extractErrorDetail(err));
        });
    }

    const handleFiles = async (handledFiles: FileList | null) => {
        if (!handledFiles) return;
        if (!validateTrailerFile(handledFiles[0])) {
            setError(t('modal:UPLOAD_TRAILER_ERROR_FORMAT'));
            return;
        }
        const f = handledFiles[0];
        setFilename(f.name);
        setProgress(0);

        uploadMultipart(f, FSObjectCategory.TRAILER, [], abortController.current)
            .then(() => {
                setPoll(true);
            })
            .catch(() => {
                alert.error(t('modal:DOWNLOAD_FILE_ERROR_FULL'))
                setProgress(null);
            });
    };

    return <>
        {(blocked) &&
            (blockedMsg ?
                <p className="form__uploader-warning">
                    {blockedMsg}
                </p>
                :
                <p className="form__uploader-warning">
                    <strong>
                        <Trans>The service is not available in the selected tariff.</Trans>
                    </strong>
                    {' '}
                    <Trans i18nKey="SeeTariffs">See <Link to="/tariff">tariffs</Link></Trans>
                    {/* <br />
                        <a href="#" onClick={(e) => { e.preventDefault(); setWarningDetail(true); }}>Подробнее
                            <Arrow className="m-icon-admin" />
                        </a> */}
                </p>
            )
        }
        <div className="form__uploader _video"  style={{paddingRight: moderation ? undefined : '0px'}}>
            <ErrorModal isOpen={!!error} onClose={() => setError('')} content={error} title={errorTitle} />
            {(!has && !unsaved && !progress) &&
                <FormUploaderZone
                    style={{ opacity: blocked ? 0.5 : 1 }}
                    hint={<FormUploaderZoneHint />}
                >
                    <input
                        ref={refUpload}
                        type="file"
                        disabled={blocked}
                        className="form__uploader-input"
                        data-test-id="trailerUploaderInput"
                        accept={accept}
                        onChange={async (e) => {
                            if (!blocked) {
                                if (e.target.files) {
                                    handleFiles(e.target.files);
                                }
                                e.preventDefault();
                                // chrome fix
                                e.target.value = '';
                                e.target.files = null;
                            }
                        }}
                    />
                    {label}
                    <br />
                    <Trans>
                        Supported file formats mp4, webm и ogg
                    </Trans>
                    <br />
                    <Trans>limit</Trans> 1 Gb
                </FormUploaderZone>
            }
            {(!!progress && !has && !unsaved) &&
                <div className="form__uploader-progress" style={{ display: 'block' }}>
                    <div className={'form__uploader-progress-item'} style={{ display: 'block' }}>
                        <div className="form__uploader-progress-item">
                            <div className="form__uploader-progress-item-icon">
                                <FileShadow />
                            </div>
                            <div className="form__uploader-progress-item-bar">
                                <div className="form__uploader-progress-item-bar-name">{filename}</div>
                                <div className="form__uploader-progress-item-bar-line">
                                    <div className="form__uploader-progress-item-bar-line-bar">
                                        <div className="form__uploader-progress-item-bar-line-bar-inner" style={{ width: `${progress}%` }}>
                                        </div>
                                    </div>
                                    <div className="form__uploader-progress-item-bar-line-percentage">{progress}%</div>
                                </div>
                            </div>
                            <div className="form__uploader-progress-item-cancel" onClick={async () => await remove()}>
                                <Trans>Cancel</Trans>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {has &&
                <div className="form__uploader-image" style={{ display: 'block' }}>
                    <figure className={'video-player' + (active ? ' _active' : '')}>
                        <video ref={videoRef} playsInline className="video-player__video" controls={active}>
                            <source src={BASE_URL.substring(0, BASE_URL.length - 1) + show_url} type="video/mp4" />
                        </video>
                        {ageLimit && !sessionStorage.agelimitconfirm ?
                            <div className="video-player__agelimit" onClick={() => { const ret = confirm(t('Please confirm that you are over 18')); if (ret) { setActive(true); sessionStorage.agelimitconfirm = true; videoRef.current?.play(); } }}>
                            </div>
                            :
                            <div className="video-player__play" onClick={() => { setActive(true); videoRef.current?.play(); }}>
                            </div>
                        }
                    </figure>
                    <div className="form__uploader-image-remove" onClick={() => {
                        const ret = confirm(t('Are you sure you want to delete this trailer?'));
                        if (ret) {
                            delete_url && deleteTrailer(delete_url).unwrap()
                                .then(() => {
                                    setProgress(null);
                                    onChange && onChange(-1);
                                })
                                .catch(e => {
                                    setError(extractErrorDetail(e));
                                });
                            onDelete && onDelete();
                            setProgress(null);
                        }
                    }}>
                        <Close />
                    </div>
                </div>
            }
            {unsaved &&
                <div className="form__uploader-image" style={{ display: 'block' }}>
                    <figure className={'video-player' + (active ? ' _active' : '')}>
                        <video ref={videoRef} playsInline className="video-player__video" controls={active}>
                            <source src={BASE_URL.substring(0, BASE_URL.length - 1) + unsaved} type="video/mp4" />
                        </video>
                        {ageLimit && !sessionStorage.agelimitconfirm ?
                            <div className="video-player__agelimit" onClick={() => { const ret = confirm(t('Please confirm that you are over 18')); if (ret) { setActive(true); sessionStorage.agelimitconfirm = true; videoRef.current?.play(); } }}>
                            </div>
                            :
                            <div className="video-player__play" onClick={() => { setActive(true); videoRef.current?.play(); }}>
                            </div>
                        }
                    </figure>
                    <div className="form__uploader-image-remove" onClick={() => {
                        const ret = confirm(t('Are you sure you want to delete this trailer?'));
                        ret && fileId && deleteFile(fileId).unwrap()
                            .then(() => {
                                setProgress(null);
                                setFileId(null);
                                setUnsaved('');
                                onChange && onChange(-1);
                            })
                            .catch(e => {
                                setError(extractErrorDetail(e));
                            });
                    }}>
                        <Close />
                    </div>
                </div>
            }
            {moderation && <>
                <FormEntryStatus value={checked} setValue={(v) => setChecked && setChecked(v)} editable={moderateable} />
                <FormEntryComment value={comment} setValue={(v) => setComment && setComment(v)} editable={moderateable} />
            </>}
        </div>
    </>
}

export default UnitTrailerUploaderV1;
